import { hot } from 'react-hot-loader/root'
import React, { useEffect } from 'react'
import { Router, Location } from '@reach/router'
import { ModalShell, ErrorBoundary } from 'components/common'
import { isModalView, hasFeature } from 'utils/server-data'
import { setNewFlowModal, isGBDebitCardEnrollmentFlow } from 'utils/flow'
import { CLIENT_ROUTE_PATHS } from 'utils/routes'
import { Global } from '@emotion/core'
import globalStyles from './global-styles'
import {
  // v1 Flows
  EnrollVirtualCard,
  CancelCard,
  CancelCardProduct,
  ChangePin,
  Summary,
  ViewPin,
  Funding,
  FundingProduct,
  ChangeCvv,
  NotFound,
  ProductEligibilityResult,

  // v2 Flows
  EnrollCardFlow,
  ActivateCardFlow,
  ChangeAddressFlow,
  AddAddressFlow,
  ReplaceCardFlow,
  SetPinFlow,
  StaticPageFlow,
  ShowCardFlow,
  LowBalanceAlertSettingsFlow,
  RequestPhysicalCardFlow,
  XoPpdcFlow,
  SetupFI,

  // Main Page Template
  ManageCard,
  Rewards,
} from './app-flows'

const {
  DECOMMISSIONED_ENROLLMENT,
  ENROLL_CARD,
  PRODUCT_ELIGIBILITY,
  SEARCH,
  SUMMARY,
  REWARDS,
  XO_PPDC,
  FUNDING,
  FUNDING_PRODUCT,
  CANCEL_CARD_BY_PRODUCT,
  LOW_BALANCE_ALERT_SETTINGS,
  CHANGE_PIN,
  VIEW_PIN,
  CHANGE_CVV,
  CANCEL_CARD,
  CHANGE_ADDRESS,
  ADD_ADDRESS,
  REPLACE_CARD,
  ACTIVATE_CARD,
  SET_PIN,
  STATIC_PAGE,
  PRODUCT_STATIC_PAGE,
  SHOW_CARD,
  REQUEST_PHYSICAL_CARD,
  SETUP_FUNDING
} = CLIENT_ROUTE_PATHS

function App() {
  // Set body attribute for GB enrollment flows to enable conditional styling
  useEffect(() => {
    const isGBEnrollment = isGBDebitCardEnrollmentFlow()
    
    if (isGBEnrollment) {
      document.body.setAttribute('data-gb-enrollment', 'true')
    } else {
      document.body.removeAttribute('data-gb-enrollment')
    }
    
    if(document.body.hasAttribute('data-pin-create-flow') === true) {
      document.body.removeAttribute('data-pin-create-flow')
    }

    // Cleanup on unmount
    return () => {
      document.body.removeAttribute('data-gb-enrollment')
    }
  }, [])

  return (
    <ErrorBoundary>
      <Global styles={globalStyles} />
      <Location>
        {({ location }) => {
          if (isModalView()) {
            setNewFlowModal(location.pathname)
          }

          return (
            <React.Suspense fallback={<ModalShell />}>
              <Router>
                {/* v1 Flows */}
                <EnrollVirtualCard path={DECOMMISSIONED_ENROLLMENT} />
                <Summary path={SEARCH} />
                <ShowCardFlow path={SHOW_CARD} />
                <CancelCard path={CANCEL_CARD} />
                <ChangePin path={CHANGE_PIN} />
                <ViewPin path={VIEW_PIN} />

                {/* TODO Can the funding URL can be dynamic based on type? */}
                <Funding path={FUNDING} />
                <ProductEligibilityResult path={PRODUCT_ELIGIBILITY} />

                {/* TODO Decommissioned - To review and remove later */}
                {/* Temporary for Virtual Card until one card management page unifies physical and virtual */}
                <CancelCardProduct path={CANCEL_CARD_BY_PRODUCT} />
                <ChangeCvv path={CHANGE_CVV} />
                <FundingProduct path={FUNDING_PRODUCT} />

                {/* v2 Flows */}
                <EnrollCardFlow path={ENROLL_CARD} />
                <ActivateCardFlow path={ACTIVATE_CARD} />
                <ChangeAddressFlow path={CHANGE_ADDRESS} />
                <AddAddressFlow path={ADD_ADDRESS} />
                <ReplaceCardFlow path={REPLACE_CARD} />
                <SetPinFlow path={SET_PIN} />
                <LowBalanceAlertSettingsFlow path={LOW_BALANCE_ALERT_SETTINGS} />
                <StaticPageFlow path={PRODUCT_STATIC_PAGE} />
                <StaticPageFlow path={STATIC_PAGE} />
                <RequestPhysicalCardFlow path={REQUEST_PHYSICAL_CARD} />
                <SetupFI path={SETUP_FUNDING} />

                {/* XO-PPDC Flow */}
                <XoPpdcFlow path={XO_PPDC} />
                {/* Main page template */}
                <ManageCard path={SUMMARY} />
                {hasFeature('enableRewardsExperience') && <Rewards path={REWARDS} />}
                <NotFound default />
              </Router>
            </React.Suspense>
          )
        }}
      </Location>
    </ErrorBoundary>
  )
}

export default hot(App)
